import { useState, useEffect } from 'react';
import { Input, FormFeedback } from 'reactstrap';
import { GradientDecorationLine, BaseCustomButton, BaseCustomTextArea, DateUtil, BaseSelectComponent } from '../../../shared';

export const SampleMassiveTab = ({
  samples,
  setSamples,
  setShowModal,
  setActiveTab,
  formikFormData,
  resetFormAndCloseModal,
  isUpdate,
  isClone,
  rowInfo,
  sampleCount,
  initialSampleDate,
  initialKind,
  initialRegion,
  initialUnitMeasure,
  initialQuantity,
  unitMeasures = [],
  kinds = [],
  regions = [],
}) => {
  const [sampleErrors, setSampleErrors] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [prevInitialKind, setPrevInitialKind] = useState(initialKind);
  const [prevInitialRegion, setPrevInitialRegion] = useState(initialRegion);
  const [prevInitialUnitMeasure, setPrevInitialUnitMeasure] = useState(initialUnitMeasure);
  const [prevInitialQuantity, setPrevInitialQuantity] = useState(initialQuantity);

  // Effect to update state when window resizes
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to prefill form when editing an existing record
  useEffect(() => {
    if (sampleCount === 1 && rowInfo && (isClone || isUpdate)) {
      setSamples([
        {
          description: rowInfo.name || "",
          date: rowInfo.dateAt ? DateUtil.formatDisplay(rowInfo.dateAt) : "",
          kind: rowInfo.kind?.id || "",
          region: rowInfo.region?.id || "",
          unitMeasure: rowInfo.unitMeasure?.id || "",
          quantity: rowInfo.quantity ?? "",
        }
      ]);
    }
  }, [isUpdate, rowInfo, formikFormData.isValidating, isClone, sampleCount, setSamples]);

  useEffect(() => {
    if (initialSampleDate) {
      setSamples(prevSamples =>
        prevSamples.map(sample => ({
          ...sample,
          date: initialSampleDate,
        }))
      );
    }
  }, [initialSampleDate]);

  useEffect(() => {
    if (initialKind && initialKind !== prevInitialKind) {
      setSamples(prevSamples =>
        prevSamples.map(sample => ({
          ...sample,
          kind: sample.kind === prevInitialKind || !sample.kind ? initialKind : sample.kind,
        }))
      );
      setPrevInitialKind(initialKind);
    }
  }, [initialKind, prevInitialKind]);

  useEffect(() => {
    if (initialRegion && initialRegion !== prevInitialRegion) {
      setSamples(prevSamples =>
        prevSamples.map(sample => ({
          ...sample,
          region: sample.region === prevInitialRegion || !sample.region ? initialRegion : sample.region,
        }))
      );
      setPrevInitialRegion(initialRegion);
    }
  }, [initialRegion, prevInitialRegion]);

  useEffect(() => {
    if (initialUnitMeasure && initialUnitMeasure !== prevInitialUnitMeasure) {
      setSamples(prevSamples =>
        prevSamples.map(sample => ({
          ...sample,
          unitMeasure: sample.unitMeasure === prevInitialUnitMeasure || !sample.unitMeasure ? initialUnitMeasure : sample.unitMeasure,
        }))
      );
      setPrevInitialUnitMeasure(initialUnitMeasure);
    }
  }, [initialUnitMeasure, prevInitialUnitMeasure]);

  useEffect(() => {
    if (initialQuantity !== undefined && initialQuantity !== prevInitialQuantity) {
      setSamples(prevSamples =>
        prevSamples.map(sample => ({
          ...sample,
          quantity: sample.quantity === prevInitialQuantity || sample.quantity === undefined || sample.quantity === "" ? initialQuantity : sample.quantity,
        }))
      );
      setPrevInitialQuantity(initialQuantity);
    }
  }, [initialQuantity, prevInitialQuantity]);

  const handleSampleChange = (index, field, value) => {
    const sanitizedValue = value === "-" ? "" : value; // Replace "-" with an empty string
    setSamples(prevSamples => {
      const updatedSamples = [...prevSamples];
      updatedSamples[index] = { ...updatedSamples[index], [field]: sanitizedValue };
      return updatedSamples;
    });

    setSampleErrors(prevErrors => {
      const updatedErrors = [...prevErrors];
      updatedErrors[index] = { ...updatedErrors[index], [field]: null };
      return updatedErrors;
    });
  };

  const handleSave = async () => {
    const errors = await formikFormData.validateForm();
    formikFormData.setTouched(errors);

    let validationErrors = Object.entries(errors).map(([field, message]) => ({
      field, message
    }));

    let newSampleErrors = [];
    samples.forEach((sample, index) => {
      let sampleError = {};
      if (!sample.description || sample.description.trim() === "") {
        sampleError.description = "Ingrese una descripción válida";
        validationErrors.push({ field: `Muestra ${index + 1} (Descripción)`, message: "Ingrese una descripción válida" });
      }
      if (!sample.date || sample.date.trim() === "") {
        sampleError.date = "Ingrese una fecha válida";
        validationErrors.push({ field: `Muestra ${index + 1} (Fecha)`, message: "Ingrese una fecha válida" });
      }
      if (!sample.unitMeasure || String(sample.unitMeasure).trim() === "") {
        sampleError.unitMeasure = "Seleccione U. Medida";
        validationErrors.push({ field: `Muestra ${index + 1} (Unidad de Medida)`, message: "Seleccione Unidad de Medida" });
      }
      if (sample.quantity === undefined || sample.quantity === "" || Number(sample.quantity) < 1) {
        sampleError.quantity = "Ingrese cantidad válida";
        validationErrors.push({ field: `Muestra ${index + 1} (Cantidad)`, message: "Ingrese una cantidad válida" });
      }
      if (!sample.kind || sample.kind.trim() === "") {
        sampleError.kind = "Seleccione Clase";
        validationErrors.push({ field: `Muestra ${index + 1} (Clase)`, message: "Seleccione Clase" });
      }
      if (!sample.region || sample.region.trim() === "") {
        sampleError.region = "Seleccione Localidad";
        validationErrors.push({ field: `Muestra ${index + 1} (Localidad)`, message: "Seleccione Localidad" });
      }
      newSampleErrors.push(sampleError);
    });

    setSampleErrors(newSampleErrors);

    if (validationErrors.length > 0) return;

    formikFormData.setFieldValue("resetActiveTab", () => setActiveTab("1"));
    formikFormData.handleSubmit();
    setShowModal(false);
  };

  return (
    <div className='mt-3'>
      {/* Headers appear only on screens larger than md */}
      {!isSmallScreen && (
        <div className='row align-items-center text-center mb-2' style={{ flexWrap: 'nowrap' }}>
          <label className='text-primary' style={{ flex: '0 0 3.5%', maxWidth: '3.5%' }}>#</label>
          <label className='text-primary' style={{ flex: '0 0 11%', maxWidth: '11%' }}>Fecha de muestreo</label>
          <label className='text-primary' style={{ flex: '0 0 11%', maxWidth: '11%' }}>Unidad de Medida</label>
          <label className='text-primary' style={{ flex: '0 0 6.5%', maxWidth: '6.5%' }}>Cantidad</label>
          <label className='text-primary' style={{ flex: '0 0 12%', maxWidth: '12%' }}>Clase</label>
          <label className='text-primary' style={{ flex: '0 0 16.5%', maxWidth: '16.5%' }}>Localidad</label>
          <label className='text-primary' style={{ flex: '0 0 39.5%', maxWidth: '39.5%' }}>Descripción</label>
        </div>
      )}

      <div className='mb-5'>
        {samples.map((sample, index) => (
          <div key={index} className="mb-2">
            <div className="row align-items-center" style={{ flexWrap: isSmallScreen ? 'wrap' : 'nowrap' }}>
              <div className={isSmallScreen ? "col-4" : ""} style={isSmallScreen ? {} : { flex: '0 0 3.5%', maxWidth: '3.5%', paddingLeft: '2px', paddingRight: '2px' }}>
                <Input className="form-control text-center fw-bold px-1" style={{ height: '38px' }} value={`${isSmallScreen ? 'Muestra ' : ''}${index + 1}`} disabled />
              </div>
              
              {isSmallScreen &&
                <label className='text-primary col-4 text-end mt-2'>Fecha muestreo</label>
              }
              <div className={isSmallScreen ? "col-4" : ""} style={isSmallScreen ? {} : { flex: '0 0 11%', maxWidth: '11%', paddingLeft: '2px', paddingRight: '2px' }}>
                <Input
                  type="date"
                  value={sample.date}
                  onChange={(e) => handleSampleChange(index, "date", e.target.value)}
                  style={{ height: '38px' }}
                  className={`form-control ${sampleErrors[index]?.date ? "is-invalid" : ""}`}
                />
                {sampleErrors[index]?.date && (
                  <FormFeedback type="invalid">{sampleErrors[index]?.date}</FormFeedback>
                )}
              </div>

              {isSmallScreen &&
                <label className='text-primary col-4 text-end mt-2'>Unidad Medida</label>
              }
              <div className={isSmallScreen ? "col-8" : ""} style={isSmallScreen ? {} : { flex: '0 0 11%', maxWidth: '11%', paddingLeft: '2px', paddingRight: '2px' }}>
                <BaseSelectComponent
                  options={unitMeasures.map(dir => ({ value: dir.id, label: dir.name }))}
                  value={sample.unitMeasure}
                  onChange={(value) => handleSampleChange(index, "unitMeasure", value)}
                  placeholder="U. Medida"
                  error="Seleccione U. Medida"
                  touched={!!sampleErrors[index]?.unitMeasure}
                  status={sampleErrors[index]?.unitMeasure ? 'error' : ''}
                  style={{ height: '38px' }}
                />
              </div>

              {isSmallScreen &&
                <label className='text-primary col-4 text-end mt-2'>Cantidad</label>
              }
              <div className={isSmallScreen ? "col-4" : ""} style={isSmallScreen ? {} : { flex: '0 0 6.5%', maxWidth: '6.5%', paddingLeft: '2px', paddingRight: '2px' }}>
                <Input
                  type="number"
                  min="1"
                  value={sample.quantity}
                  onChange={(e) => handleSampleChange(index, "quantity", e.target.value)}
                  onFocus={(e) => e.target.select()}
                  style={{ height: '38px' }}
                  className={`form-control text-center px-1 ${sampleErrors[index]?.quantity ? "is-invalid" : ""}`}
                />
                {sampleErrors[index]?.quantity && (
                  <FormFeedback type="invalid">{sampleErrors[index]?.quantity}</FormFeedback>
                )}
              </div>

              {isSmallScreen &&
                <label className='text-primary col-4 text-end mt-2'>Clase</label>
              }
              <div className={isSmallScreen ? "col-8" : ""} style={isSmallScreen ? {} : { flex: '0 0 12%', maxWidth: '12%', paddingLeft: '2px', paddingRight: '2px' }}>
                <BaseSelectComponent
                  options={kinds.map(dir => ({ value: dir.id, label: dir.name }))}
                  value={sample.kind}
                  onChange={(value) => handleSampleChange(index, "kind", value)}
                  placeholder="Clase"
                  error="Seleccione Clase"
                  touched={!!sampleErrors[index]?.kind}
                  status={sampleErrors[index]?.kind ? 'error' : ''}
                  style={{ height: '38px' }}
                />
              </div>

              {isSmallScreen &&
                <label className='text-primary col-4 text-end mt-2'>Localidad</label>
              }
              <div className={isSmallScreen ? "col-8" : ""} style={isSmallScreen ? {} : { flex: '0 0 16.5%', maxWidth: '16.5%', paddingLeft: '2px', paddingRight: '2px' }}>
                <BaseSelectComponent
                  options={regions.map(dir => ({ value: dir.id, label: `${dir.state}-${dir.municipality}` }))}
                  value={sample.region}
                  onChange={(value) => handleSampleChange(index, "region", value)}
                  placeholder="Localidad"
                  error="Seleccione Localidad"
                  touched={!!sampleErrors[index]?.region}
                  status={sampleErrors[index]?.region ? 'error' : ''}
                  style={{ height: '38px' }}
                />
              </div>

              {isSmallScreen &&
                <label className='text-primary col-12 mt-3'>Descripción</label>
              }
              <div className={isSmallScreen ? "col-12" : ""} style={isSmallScreen ? {} : { flex: '0 0 39.5%', maxWidth: '39.5%', paddingLeft: '2px', paddingRight: '2px' }}>
                <BaseCustomTextArea
                  placeholder="Descripción de Muestra"
                  value={sample.description}
                  onChange={(e) => handleSampleChange(index, "description", e.target.value)}
                  className={`form-control ${sampleErrors[index]?.description ? "is-invalid" : ""}`}
                  style={{ height: '38px', minHeight: '38px', resize: 'none' }}
                  rows={1}
                />
                {sampleErrors[index]?.description && (
                  <FormFeedback type="invalid">{sampleErrors[index]?.description}</FormFeedback>
                )}
              </div>
            </div>
            {/* <hr /> only appears on small screens and is not rendered for the last sample */}
            {isSmallScreen && index !== samples.length - 1 && <hr className='my-4' />}
          </div>
        ))}
      </div>

      <GradientDecorationLine />

      <div className="d-flex justify-content-end mt-4">
        <BaseCustomButton
          outline
          label='Atrás'
          color="success"
          className='rounded-3 me-4'
          icon='mdi mdi-arrow-left me-2'
          onClick={() => setActiveTab("1")}
        />
        <BaseCustomButton
          outline
          color="primary"
          label='Guardar'
          className="rounded-3 me-4"
          icon='mdi mdi-content-save me-1'
          onClick={handleSave}
        />
        <BaseCustomButton
          outline
          label='Cancelar'
          color="danger"
          className="rounded-3"
          icon='mdi mdi-close-circle-outline me-2'
          onClick={resetFormAndCloseModal}
        />
      </div>
    </div>
  );
};